"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import { sanitizeHtml } from "@/lib/sanitize";
import FaqJsonLd from "./FaqJsonLd";

interface FaqItem {
  id: string;
  question: string;
  answerHtml: string;
}

interface FaqProps {
  items: FaqItem[];
  allowMultiple?: boolean;
  defaultOpenId?: string;
  subtitle?: string;
}

export default function Faq({
  items,
  allowMultiple = false,
  defaultOpenId,
  subtitle = "Find answers to common questions about our group cards and services.",
}: FaqProps) {
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const [mounted, setMounted] = useState(false);
  const itemRefs = useRef<Map<string, HTMLElement>>(new Map());

  // Initialize state with default open item
  useEffect(() => {
    setMounted(true);
    if (defaultOpenId && items.find((item) => item.id === defaultOpenId)) {
      setOpenIds(new Set([defaultOpenId]));
    }
  }, [defaultOpenId, items]);

  // Handle hash navigation for deep linking
  useEffect(() => {
    if (!mounted) return;

    const handleHashChange = () => {
      const hash = window.location.hash.replace("#faq-", "");
      if (hash && items.find((item) => item.id === hash)) {
        const newOpenIds = allowMultiple
          ? new Set<string>(openIds)
          : new Set<string>();
        newOpenIds.add(hash);
        setOpenIds(newOpenIds);

        // Focus the button after a short delay to ensure content is rendered
        setTimeout(() => {
          const button = itemRefs.current.get(hash)?.querySelector("button");
          if (button) {
            button.focus();
          }
        }, 100);
      }
    };

    // Handle initial hash
    if (window.location.hash.startsWith("#faq-")) {
      handleHashChange();
    }

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [mounted, items, allowMultiple, openIds]);

  const toggleItem = useCallback(
    (id: string) => {
      setOpenIds((prev) => {
        const newOpenIds = new Set(prev);

        if (newOpenIds.has(id)) {
          // Close the item
          newOpenIds.delete(id);
        } else {
          // Open the item
          if (!allowMultiple) {
            // Single-open: clear all others
            newOpenIds.clear();
          }
          newOpenIds.add(id);
        }

        return newOpenIds;
      });
    },
    [allowMultiple]
  );

  const expandAll = useCallback(() => {
    setOpenIds(new Set(items.map((item) => item.id)));
  }, [items]);

  const collapseAll = useCallback(() => {
    setOpenIds(new Set());
  }, []);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, currentIndex: number) => {
      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();
        const direction = event.key === "ArrowUp" ? -1 : 1;
        const nextIndex =
          (currentIndex + direction + items.length) % items.length;
        const nextButton = itemRefs.current
          .get(items[nextIndex].id)
          ?.querySelector("button");
        if (nextButton) {
          (nextButton as HTMLElement).focus();
        }
      }
    },
    [items]
  );

  // Empty state
  if (!items || items.length === 0) {
    return (
      <section className="w-full mt-12 mb-8">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-500 text-center py-8">
            No frequently asked questions available at this time.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full">
      <FaqJsonLd items={items} />

      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-heart-600">Good to know</p>
          <h2 className="text-3xl tracking-tight text-ink mb-3 sm:text-4xl">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-600 text-base max-w-2xl mx-auto">
            {subtitle}
          </p>
        </div>

        {/* Expand/Collapse controls */}
        {allowMultiple && items.length > 1 && (
          <div className="flex justify-center gap-4 mb-8">
            <button
              onClick={expandAll}
              className="px-4 py-2 text-sm font-medium text-ink hover:bg-paper rounded-lg transition-colors"
            >
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
            >
              Collapse All
            </button>
          </div>
        )}

        {/* FAQ Items */}
        <div className="space-y-4">
          {items.map((item, index) => {
            const isOpen = openIds.has(item.id);

            return (
              <div
                key={item.id}
                ref={(el) => {
                  if (el) {
                    itemRefs.current.set(item.id, el);
                  }
                }}
                className="bg-white rounded-2xl border border-paper-rule transition-colors duration-200 hover:border-ink-300"
              >
                {/* Header Button */}
                <button
                  onClick={() => toggleItem(item.id)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  aria-expanded={isOpen}
                  aria-controls={`faq-panel-${item.id}`}
                  className="w-full px-6 py-4 text-left flex items-center justify-between focus:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-inset rounded-2xl transition-colors duration-200"
                >
                  <h3 className="font-sans text-base font-semibold text-ink pr-4">
                    {item.question}
                  </h3>

                  {/* Chevron Icon */}
                  <svg
                    className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 flex-shrink-0 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {/* Content Panel */}
                <div
                  id={`faq-panel-${item.id}`}
                  role="region"
                  aria-labelledby={`faq-button-${item.id}`}
                  className={`overflow-hidden transition-all duration-300 ease-out ${
                    isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                  }`}
                >
                  <div className="px-6 pb-5 text-gray-700 leading-relaxed">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: mounted
                          ? sanitizeHtml(item.answerHtml)
                          : item.answerHtml,
                      }}
                      className="prose prose-sm max-w-none [&_a]:text-heart-700 [&_a]:underline [&_a:hover]:text-heart-800 [&_strong]:font-semibold [&_strong]:text-ink"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
